import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { LeadershipApiService } from '@angular-workspace/services';
import { SubHeaderComponent } from '../../shared/components/sub-header/sub-header.component';
import { FooterComponent } from '../../shared/components/footer/footer.component';
import { LeaderboardProvider } from './provider/leaderboard-provider';
import { LeaderboardAssets } from './assets/leaderboard-assets';
import { LeaderboardOrganisation } from './organisation/leaderboard-organisation';

export interface LeadershipMember {
  id: number;
  rank: number;
  name: string;
  role: string;
  title: string;
  totalPublished?: number;
  likes?: number;
  downloads?: number;
  views?: number;
  organizationName?: string;
  assetType?: string;
  members?: number;
}

export type LeadershipCategory = 'providers' | 'assets' | 'organisation';

export interface LeaderboardTabConfig {
  key: LeadershipCategory;
  label: string;
  iconActive: string;
  iconInactive: string;
}

@Component({
  selector: 'app-leaderboard-home',
  standalone: true,
  imports: [
    CommonModule,
    SubHeaderComponent,
    FooterComponent,
    LeaderboardProvider,
    LeaderboardAssets,
    LeaderboardOrganisation,
  ],
  templateUrl: './leaderboard-home.html',
  styleUrl: './leaderboard-home.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LeaderboardHome implements OnInit {
  private readonly leadershipApiService = inject(LeadershipApiService);

  readonly subHeaderBadgeText = 'Healthcare Analytics';
  readonly subHeaderTitle = 'Leaderboard';
  readonly subHeaderDescription =
    'Explore the highest-performing organisations across the exchange based on published assets, community upvotes, and downloads. The content mirrors the platform data while presenting it in a clearer, more professional leaderboard experience.';
  readonly subHeaderIconSrc = '/icons/trophy.svg';

  readonly categoryTabs: ReadonlyArray<LeaderboardTabConfig> = [
    {
      key: 'providers',
      label: 'Providers',
      iconActive: '/icons/building-black.svg',
      iconInactive: '/icons/building.svg',
    },
    {
      key: 'assets',
      label: 'Assets',
      iconActive: '/icons/server-blue.svg',
      iconInactive: '/icons/server-blue.svg',
    },
    {
      key: 'organisation',
      label: 'Organisation',
      iconActive: '/icons/vector-1.svg',
      iconInactive: '/icons/vector-1.svg',
    },
  ];

  readonly activeCategory = signal<LeadershipCategory>('providers');
  readonly isLoading = signal(false);
  readonly pageSize = 10;

  readonly leadershipMembers = signal<LeadershipMember[]>([]);

  readonly providersPagination = signal({
    currentPage: 1,
    pageSize: this.pageSize,
    hasNext: false,
    totalCount: 0,
    isLoading: false,
  });

  readonly assetsPagination = signal({
    currentPage: 1,
    pageSize: this.pageSize,
    hasNext: false,
    totalCount: 0,
    isLoading: false,
  });

  readonly organisationPagination = signal({
    currentPage: 1,
    pageSize: this.pageSize,
    hasNext: false,
    totalCount: 0,
    isLoading: false,
  });

  readonly showLoadMoreProviders = computed(
    () => this.leadershipMembers().length >= this.pageSize && this.activeCategory() === 'providers',
  );
  readonly showLoadMoreAssets = computed(
    () => this.leadershipMembers().length >= this.pageSize && this.activeCategory() === 'assets',
  );
  readonly showLoadMoreOrganisation = computed(
    () =>
      this.leadershipMembers().length >= this.pageSize && this.activeCategory() === 'organisation',
  );

  async ngOnInit(): Promise<void> {
    await this.loadCategoryData('providers', 1, false);
  }

  tabIcon(tab: LeaderboardTabConfig): string {
    return this.activeCategory() === tab.key ? tab.iconActive : tab.iconInactive;
  }

  tabIconClass(tab: LeaderboardTabConfig): Record<string, boolean> {
    const active = this.activeCategory() === tab.key;
    return {
      'leaderboard-home__tab-icon': true,
      'leaderboard-home__tab-icon--active': active,
    };
  }

  async changeCategory(category: LeadershipCategory): Promise<void> {
    if (this.activeCategory() === category) {
      return;
    }
    this.activeCategory.set(category);
    await this.loadCategoryData(category, 1, false);
  }

  async loadMoreProviders(): Promise<void> {
    const p = this.providersPagination();
    if (!p.hasNext || p.isLoading) return;
    await this.loadCategoryData('providers', p.currentPage + 1, true);
  }

  async loadMoreAssets(): Promise<void> {
    const p = this.assetsPagination();
    if (!p.hasNext || p.isLoading) return;
    await this.loadCategoryData('assets', p.currentPage + 1, true);
  }

  async loadMoreOrganisations(): Promise<void> {
    const p = this.organisationPagination();
    if (!p.hasNext || p.isLoading) return;
    await this.loadCategoryData('organisation', p.currentPage + 1, true);
  }

  private resetPagination(category: LeadershipCategory): void {
    const base = {
      currentPage: 1,
      pageSize: this.pageSize,
      hasNext: false,
      totalCount: 0,
      isLoading: false,
    };
    if (category === 'providers') this.providersPagination.set({ ...base });
    else if (category === 'assets') this.assetsPagination.set({ ...base });
    else this.organisationPagination.set({ ...base });
  }

  private setCategoryLoading(category: LeadershipCategory, loading: boolean): void {
    if (category === 'providers') {
      this.providersPagination.update((s) => ({ ...s, isLoading: loading }));
    } else if (category === 'assets') {
      this.assetsPagination.update((s) => ({ ...s, isLoading: loading }));
    } else {
      this.organisationPagination.update((s) => ({ ...s, isLoading: loading }));
    }
  }

  private updatePaginationFromResponse(
    category: LeadershipCategory,
    page: number,
    response: unknown,
    itemsLength: number,
  ): void {
    let hasNext = false;
    let totalCount = 0;
    if (response && typeof response === 'object' && !Array.isArray(response)) {
      const pi = (response as Record<string, unknown>)['paginationInfo'] as
        | Record<string, unknown>
        | undefined;
      if (pi) {
        hasNext = Boolean(pi['hasNext']);
        totalCount = Number(pi['totalCount'] ?? 0);
      }
    }
    if (!hasNext) {
      hasNext = itemsLength >= this.pageSize;
    }
    const patch = {
      currentPage: page,
      hasNext,
      totalCount,
      pageSize: this.pageSize,
    };
    if (category === 'providers') {
      this.providersPagination.update((s) => ({ ...s, ...patch }));
    } else if (category === 'assets') {
      this.assetsPagination.update((s) => ({ ...s, ...patch }));
    } else {
      this.organisationPagination.update((s) => ({ ...s, ...patch }));
    }
  }

  private async loadCategoryData(
    category: LeadershipCategory,
    page: number,
    append: boolean,
  ): Promise<void> {
    if (!append) this.resetPagination(category);
    this.isLoading.set(!append);
    this.setCategoryLoading(category, true);
    try {
      let response: unknown;
      switch (category) {
        case 'providers': {
          response = await this.leadershipApiService.getLeadershipForProvider(page, this.pageSize);
          const apiMembers = this.extractResultArray(response);
          const mapped = apiMembers.map((member, index) =>
            this.mapProviderMember(member, (page - 1) * this.pageSize + index + 1),
          );
          this.leadershipMembers.set(
            append ? [...this.leadershipMembers(), ...mapped] : mapped,
          );
          this.updatePaginationFromResponse(category, page, response, apiMembers.length);
          break;
        }
        case 'assets': {
          response = await this.leadershipApiService.getLeaderboardForAssets(page, this.pageSize);
          const apiMembers = this.extractResultArray(response);
          const mapped = apiMembers.map((member, index) =>
            this.mapAssetMember(member, (page - 1) * this.pageSize + index + 1),
          );
          this.leadershipMembers.set(
            append ? [...this.leadershipMembers(), ...mapped] : mapped,
          );
          this.updatePaginationFromResponse(category, page, response, apiMembers.length);
          break;
        }
        case 'organisation': {
          response = await this.leadershipApiService.getLeaderboardForOrganisations(
            page,
            this.pageSize,
          );
          const apiMembers = this.extractResultArray(response);
          const mapped = apiMembers.map((member, index) =>
            this.mapOrganisationMember(member, (page - 1) * this.pageSize + index + 1),
          );
          this.leadershipMembers.set(
            append ? [...this.leadershipMembers(), ...mapped] : mapped,
          );
          this.updatePaginationFromResponse(category, page, response, apiMembers.length);
          break;
        }
        default:
          this.leadershipMembers.set([]);
      }
    } catch (error) {
      console.error(`Failed to load ${category} leadership data`, error);
      if (!append) this.leadershipMembers.set([]);
    } finally {
      this.isLoading.set(false);
      this.setCategoryLoading(category, false);
    }
  }

  private extractResultArray(response: unknown): Record<string, unknown>[] {
    if (Array.isArray(response)) {
      return response as Record<string, unknown>[];
    }
    if (!response || typeof response !== 'object') return [];
    const r = response as Record<string, unknown>;
    const result = r['result'];
    if (Array.isArray(result)) return result as Record<string, unknown>[];
    const data = r['data'];
    if (Array.isArray(data)) return data as Record<string, unknown>[];
    const members = r['members'];
    if (Array.isArray(members)) return members as Record<string, unknown>[];
    return [];
  }

  private mapProviderMember(
    member: Record<string, unknown>,
    fallbackRank: number,
  ): LeadershipMember {
    const rank = Number(member['rank'] ?? fallbackRank);
    const name = this.toDisplayText(member['username'] ?? member['name']);
    const orgName = this.toDisplayText(member['organizationName']);
    const role = this.toDisplayText(member['role']);
    return {
      id: Number(member['id'] ?? rank),
      rank,
      name,
      role: orgName || role,
      title: orgName || this.toDisplayText(member['title']) || 'Provider',
      totalPublished: Number(member['totalPublished'] ?? 0),
      likes: Number(member['likes'] ?? 0),
      downloads: Number(member['downloads'] ?? 0),
      views: Number(member['views'] ?? 0),
      organizationName: orgName,
      members: Number(member['members'] ?? 0),
    };
  }

  private mapAssetMember(
    member: Record<string, unknown>,
    fallbackRank: number,
  ): LeadershipMember {
    const rank = Number(member['rank'] ?? fallbackRank);
    const orgName = this.toDisplayText(member['organizationName']);
    const role = this.toDisplayText(member['role']);
    return {
      id: Number(member['id'] ?? rank),
      rank,
      name: this.toDisplayText(member['name'] ?? member['label']),
      role: orgName || role,
      title: orgName || this.toDisplayText(member['title']) || 'Asset',
      totalPublished: Number(member['totalPublished'] ?? 0),
      likes: Number(member['likes'] ?? 0),
      downloads: Number(member['downloads'] ?? 0),
      views: Number(member['views'] ?? 0),
      organizationName: orgName,
      assetType: this.formatAssetType(member['type']),
    };
  }

  private mapOrganisationMember(
    member: Record<string, unknown>,
    fallbackRank: number,
  ): LeadershipMember {
    const rank = Number(member['rank'] ?? fallbackRank);
    const orgName = this.toDisplayText(member['organizationName']);
    const name = orgName || this.toDisplayText(member['name']);
    const role = this.toDisplayText(member['role']);
    return {
      id: Number(member['id'] ?? rank),
      rank,
      name,
      role: orgName || role,
      title: orgName || this.toDisplayText(member['title']) || 'Organisation',
      totalPublished: Number(member['totalPublished'] ?? 0),
      likes: Number(member['likes'] ?? 0),
      downloads: Number(member['downloads'] ?? 0),
      views: Number(member['views'] ?? 0),
      organizationName: orgName,
      members: Number(member['members'] ?? 0),
    };
  }

  private toDisplayText(value: unknown): string {
    if (value === null || value === undefined) return '';
    const text = String(value).trim();
    if (!text) return '';
    const lower = text.toLowerCase();
    if (lower === 'undefined' || lower === 'null' || lower === 'nan') return '';
    return text;
  }

  private formatAssetType(assetType: unknown): string {
    if (Array.isArray(assetType) && assetType.length > 0) {
      return this.formatAssetType(assetType[0]);
    }
    if (typeof assetType !== 'string' || !assetType.trim()) return 'Asset';
    const raw = assetType.includes(':')
      ? assetType.split(':').pop() || assetType
      : assetType;
    const normalized = raw.toLowerCase().replace(/[\s_-]/g, '');
    if (normalized === 'aimodel' || normalized === 'model') return 'Model';
    if (normalized === 'databank' || normalized === 'dataset') return 'Dataset';
    if (normalized === 'apps' || normalized === 'app' || normalized === 'usecase')
      return 'Usecase';
    return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
  }
}
